import * as rd from '@devexperts/remote-data-ts'

import type { ReservationModel } from '@/api/createCart'
import type { CartDetailResponse, ReservationModelService } from '@/models/cart'
import type { License } from '@/models/license'
import type { QuoteReservation } from '@/models/quote'
import type { ServiceGroup } from '@/models/service'
import {
  BookingAvailability,
  type BookingAvailabilitySliceState,
} from '@/store/bookingAvailabilitySlice'
import { SESSION_STORAGE_GTM_PAYMENT_METHOD_KEY } from '@/store/sessionStorageMiddleware'
import { NO, YES } from '@/utils/constants'
import { isLogged } from '@/utils/isLogged'
import { getTimeScheduleKey } from '@/utils/timeSchedules'
import { pushOneSignalTracking } from './oneSignalTracking'

export const GTM_CURRENCY_CODE = 'EUR'

export const gtmPurchaseSentStorageKey = (orderId: number) =>
  `gtm_purchase_sent_${orderId}`

export type GaEcommerceContextItem = {
  citta: string
  provincia: string
  regione: string
  nome_stabilimento: string
  id_stabilimento: string
  subregion: string
  assicurabile_meteo: string
  beach_availability_type: string
  has_bookable_spots: boolean
  has_bookable_tickets: boolean
}

export type GaEcommerceLineItem = GaEcommerceContextItem & {
  item_id: string
  item_name: string
  price: number
  quantity: number
}

export function getBeachAvailabilityFields(
  availability: BookingAvailabilitySliceState
): Pick<
  GaEcommerceContextItem,
  'beach_availability_type' | 'has_bookable_spots' | 'has_bookable_tickets'
> {
  if (!rd.isSuccess(availability)) {
    return {
      beach_availability_type: 'none',
      has_bookable_spots: false,
      has_bookable_tickets: false,
    }
  }

  const v = availability.value
  const type = v.type

  let has_bookable_spots = false
  let has_bookable_tickets = false

  switch (type) {
    case BookingAvailability.SPOTS:
      has_bookable_spots = v.availableSpots > 0
      break
    case BookingAvailability.TICKETS:
      has_bookable_tickets = v.availableTickets > 0
      break
    case BookingAvailability.SPOTS_AND_TICKETS:
      has_bookable_spots = v.availableSpots > 0
      has_bookable_tickets = v.availableTickets > 0
      break
    case BookingAvailability.NONE:
      break
  }

  return {
    beach_availability_type: type,
    has_bookable_spots,
    has_bookable_tickets,
  }
}

export function buildGaContextItem(params: {
  license: License | null | undefined
  weatherInsurable: boolean
  bookingAvailability: BookingAvailabilitySliceState
}): GaEcommerceContextItem | null {
  if (!params.license) {
    return null
  }

  const availability = getBeachAvailabilityFields(params.bookingAvailability)

  return {
    assicurabile_meteo: params.weatherInsurable ? YES : NO,
    citta: params.license.beach.city,
    id_stabilimento: String(params.license.id),
    nome_stabilimento: params.license.beach.name,
    provincia:
      params.license.beach.stateModel?.name ?? params.license.beach.state,
    regione: params.license.beach.region,
    subregion: params.license.beach.subregion,
    ...availability,
  }
}

function gaTicketQuantityFromCartReservations(
  cartReservations: QuoteReservation[] | undefined,
  serviceId: number
): number {
  if (!cartReservations?.length) {
    return 0
  }

  let total = 0
  for (const reservation of cartReservations) {
    const byTimeSchedule = reservation.boughtServices[serviceId]
    if (!byTimeSchedule) {
      continue
    }

    for (const bought of Object.values(byTimeSchedule)) {
      const dayCount = bought.days.length
      total += bought.bought * dayCount
    }
  }

  return total
}

function gaTicketQuantityFromCartReservationsByTimeScheduleKey(
  cartReservations: QuoteReservation[] | undefined,
  serviceId: number,
  timeScheduleKey: string
): number {
  if (!cartReservations?.length) {
    return 0
  }

  let total = 0
  for (const reservation of cartReservations) {
    const byTimeSchedule = reservation.boughtServices[serviceId]
    if (!byTimeSchedule) {
      continue
    }

    const bought = byTimeSchedule[timeScheduleKey]
    if (!bought) {
      continue
    }

    const dayCount = bought.days.length
    total += bought.bought * dayCount
  }

  return total
}

function timeScheduleKeysForServiceInCart(
  cartReservations: QuoteReservation[] | undefined,
  serviceId: number
): string[] {
  const keys = new Set<string>()
  for (const reservation of cartReservations ?? []) {
    const byTimeSchedule = reservation.boughtServices[serviceId]
    if (!byTimeSchedule) {
      continue
    }

    for (const key of Object.keys(byTimeSchedule)) {
      keys.add(key)
    }
  }

  return Array.from(keys)
}

export function buildGaLineItemsFromBookingCart(params: {
  license: License | null | undefined
  selectedBookingType: string | null | undefined
  cartReservations: QuoteReservation[] | undefined
  reservationPrices: Record<string, number>
  servicesWithCartQuantity: ServiceGroup[]
  bookingAvailability: BookingAvailabilitySliceState
  weatherInsurable: boolean
}): GaEcommerceLineItem[] {
  const ctx = buildGaContextItem({
    bookingAvailability: params.bookingAvailability,
    license: params.license,
    weatherInsurable: params.weatherInsurable,
  })
  if (!ctx) {
    return []
  }

  const isTickets = params.selectedBookingType === BookingAvailability.TICKETS

  if (isTickets) {
    return params.servicesWithCartQuantity.flatMap((group) =>
      group.services.flatMap((ticket) => {
        if (ticket.timeSchedules.length === 1) {
          const quantity = gaTicketQuantityFromCartReservations(
            params.cartReservations,
            ticket.serviceId
          )

          let unitPrice = ticket.price
          if (
            ticket.timeSchedules.length === 1 &&
            ticket.quantity !== quantity
          ) {
            unitPrice =
              Math.round(
                ((unitPrice * (ticket.quantity ?? 1)) / quantity) * 100
              ) / 100
          }

          return {
            ...ctx,
            item_id: String(ticket.serviceId),
            item_name: ticket.name,
            price: unitPrice,
            quantity: quantity,
          }
        }

        const cartKeys = timeScheduleKeysForServiceInCart(
          params.cartReservations,
          ticket.serviceId
        )

        return cartKeys.flatMap((tsKey) => {
          const quantity =
            gaTicketQuantityFromCartReservationsByTimeScheduleKey(
              params.cartReservations,
              ticket.serviceId,
              tsKey
            )
          if (quantity <= 0) {
            return []
          }

          const timeSchedule = ticket.timeSchedules.find(
            (ts) => getTimeScheduleKey(ts) === tsKey
          )

          if (!timeSchedule || timeSchedule.price <= 0) {
            return []
          }

          return [
            {
              ...ctx,
              item_id: String(ticket.serviceId),
              item_name: ticket.name,
              price: timeSchedule.price,
              quantity,
            },
          ]
        })
      })
    )
  }

  return (params.cartReservations ?? []).map((reservation) => {
    const key = `_${reservation.spotType}_${reservation.spotName}`
    const unitPrice = params.reservationPrices[key] ?? 0
    return {
      ...ctx,
      item_id: String(reservation.spotName),
      item_name: reservation.spotType,
      price: unitPrice,
      quantity: 1,
    }
  })
}

function gaLineItemsFromTicketServices(
  services: ReservationModelService[],
  ctx: GaEcommerceContextItem
): GaEcommerceLineItem[] {
  const servicesByKey = new Map<
    string,
    {
      service_id: number
      start_time: string
      end_time: string
      service_name: string
      quantity: number
      totalPrice: number
    }
  >()

  for (const s of services) {
    const qty = s.bought - (Number(s.deleted) || 0)
    if (qty <= 0 || s.price <= 0) {
      continue
    }

    const start_time = s.start_time ?? ''
    const end_time = s.end_time ?? ''
    const key = `${s.service_id}|${start_time}|${end_time}`

    const aggregate = servicesByKey.get(key)
    if (aggregate) {
      aggregate.quantity += qty
      aggregate.totalPrice += s.price
    } else {
      servicesByKey.set(key, {
        end_time,
        quantity: qty,
        service_id: s.service_id,
        service_name: s.service_name,
        start_time,
        totalPrice: s.price,
      })
    }
  }

  return Array.from(servicesByKey.values()).map((aggregateService) => {
    const unitPrice =
      Math.round(
        (aggregateService.totalPrice / aggregateService.quantity) * 100
      ) / 100

    return {
      ...ctx,
      item_id: String(aggregateService.service_id),
      item_name: aggregateService.service_name,
      price: unitPrice,
      quantity: aggregateService.quantity,
    }
  })
}

export function buildGaLineItemsFromCartDetail(params: {
  license: License | null | undefined
  cart: CartDetailResponse['cart']
  bookingAvailability: BookingAvailabilitySliceState
  weatherInsurable: boolean
}): GaEcommerceLineItem[] {
  const ctx = buildGaContextItem({
    bookingAvailability: params.bookingAvailability,
    license: params.license,
    weatherInsurable: params.weatherInsurable,
  })
  if (!ctx) {
    return []
  }

  return params.cart.reservationModels
    .filter((rm) => !rm.deleted)
    .flatMap((rm) => {
      if (!rm.spot_name && !rm.spot_type) {
        return gaLineItemsFromTicketServices(rm.services, ctx)
      }

      return {
        ...ctx,
        item_id: rm.spot_name,
        item_name: rm.spot_type,
        price: rm.totalPrice,
        quantity: 1,
      }
    })
}

export function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>
): void {
  window.dataLayer.push({ ecommerce: null })
  queueMicrotask(() => {
    window.dataLayer.push({
      Loggato: isLogged() ? YES : NO,
      ecommerce,
      event,
    })
  })
}

export function pushInsertDataCheckoutSequence(
  items: GaEcommerceLineItem[]
): void {
  const payload = { currency: GTM_CURRENCY_CODE, items: [...items] }
  pushEcommerceEvent('view_cart', payload)
  pushEcommerceEvent('begin_checkout', payload)
  pushEcommerceEvent('add_shipping_info', payload)
  pushOneSignalTracking('begin_checkout', { ecommerce: payload })
}

function buildPaymentDetailsDataLayerFields(params: {
  cart: CartDetailResponse['cart']
  dayDifference: number
  deposit: number
  total: number
}): Record<string, number | string> {
  const { cart, dayDifference, deposit, total } = params

  const stripeFees = cart.reservationModels.reduce(
    (acc, curr) => acc + curr.online_payment_fees,
    0
  )

  const stripeCost = cart.reservationModels.reduce(
    (acc, curr) => acc + curr.portal_payment_fees,
    0
  )

  const transactionAmount = cart.reservationModels.reduce((acc, curr) => {
    if (curr.forced_total) {
      return acc + curr.forced_total
    }
    if (curr.forced_beach) {
      return acc + (curr.forced_beach - curr.list_beach)
    }
    return curr.list_total
  }, 0)

  const cartNetValue = total - stripeFees

  return {
    Acconto: deposit > 0 ? YES : NO,
    'Assicurazione meteo': cart.weatherPolicy?.price ? YES : NO,
    'Commissione Spiagge': cart.totalFeesValue,
    'Commissione transazione': stripeFees,
    'Costo Stripe': stripeCost,
    Margine: stripeFees + (10 * 100) / cartNetValue - stripeCost,
    'Numero giorni': dayDifference,
    'Percentuale acconto': (deposit / total) * 100,
    Transato: transactionAmount,
    'Valore carrello': total,
    'Valore carrello netto': cartNetValue,
  }
}

export type EnhancedConversionsUserData = {
  email: string
  phone_number?: string
  address?: {
    first_name?: string
    last_name?: string
    street?: string
    city?: string
    region?: string
    postal_code?: string
    country?: string
  }
}

export function buildEnhancedConversionsUserDataFromReservation(
  rm: ReservationModel
): EnhancedConversionsUserData | null {
  const email = rm.email?.trim()
  if (!email) {
    return null
  }

  const phoneE164 = `${(rm.phone_area_code ?? '').trim()}${(rm.phone_number ?? '').trim()}`

  const line1 = rm.invoice_address1?.trim()
  const line2 = rm.invoice_address2?.trim()
  const street = [line1, line2].filter(Boolean).join(' ')

  const address: NonNullable<EnhancedConversionsUserData['address']> = {}
  const firstName = rm.first_name?.trim()
  const lastName = rm.last_name?.trim()
  const city = rm.invoice_city?.trim()
  const region = rm.invoice_state?.trim()
  const postal = rm.invoice_zip?.trim()
  const country = rm.invoice_country?.trim()

  if (firstName) {
    address.first_name = firstName
  }
  if (lastName) {
    address.last_name = lastName
  }
  if (street) {
    address.street = street
  }
  if (city) {
    address.city = city
  }
  if (region) {
    address.region = region
  }
  if (postal) {
    address.postal_code = postal
  }
  if (country) {
    address.country = country
  }

  return {
    email,
    ...(phoneE164 ? { phone_number: phoneE164 } : {}),
    ...(Object.keys(address).length > 0 ? { address } : {}),
  }
}

export function pushPurchaseDataLayerEvent(payload: {
  ecommerce: {
    coupon?: string
    currency: string
    items: GaEcommerceLineItem[]
    payment_method: string
    tax: number
    transaction_id: string
    value: number
  }
  paymentInfo: {
    cart: CartDetailResponse['cart']
    dayDifference: number
    deposit: number
    total: number
  }
  reservationModel?: ReservationModel
}): void {
  const paymentDetails = buildPaymentDetailsDataLayerFields({
    ...payload.paymentInfo,
  })

  const userData =
    payload.reservationModel &&
    buildEnhancedConversionsUserDataFromReservation(payload.reservationModel)

  window.dataLayer.push({ ecommerce: null })
  queueMicrotask(() => {
    window.dataLayer.push({
      ...paymentDetails,
      Loggato: isLogged() ? YES : NO,
      ecommerce: payload.ecommerce,
      event: 'purchase',
      ...(userData ? { user_data: userData } : {}),
    })

    pushOneSignalTracking('purchase', {
      ...paymentDetails,
      ecommerce: payload.ecommerce,
      ...(userData ? { user_data: userData } : {}),
    })
  })
}

export function readGtmPaymentMethodFromSession(): string {
  return sessionStorage.getItem(SESSION_STORAGE_GTM_PAYMENT_METHOD_KEY) ?? ''
}

export function writeGtmPaymentMethodToSession(method: string): void {
  sessionStorage.setItem(SESSION_STORAGE_GTM_PAYMENT_METHOD_KEY, method)
}
